/**
 * Initialise Datadog RUM + Session Replay + browser logs, or do nothing.
 *
 * Browser telemetry config is baked at build time as NEXT_PUBLIC_DD_* (the B1
 * ruling: Terraform cannot deliver browser config — NEXT_PUBLIC_* values are
 * inlined into the client bundle at build time). The client token is public by
 * design: it ships to every browser, the same rationale the Dockerfile
 * documents for the Contentful keys.
 *
 * `defaultPrivacyLevel: 'mask'` is the load-bearing privacy decision. It
 * records layout but never content — employee names, emails, and org-chart
 * text never reach Datadog. Session Replay of a masked page still captures
 * where a user got stuck, which is the value for a B2B tool. Do not weaken it
 * without revisiting the privacy decision.
 *
 * Returns whether RUM was actually initialised. Never throws — a telemetry
 * failure must not break the page.
 */

import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

export interface RumConfig {
  applicationId: string;
  clientToken: string;
  version: string;
  env: string;
  enabled: boolean;
}

export function initRum(config: RumConfig): boolean {
  if (!config.enabled) {
    return false;
  }

  let rumInitialised = false;
  try {
    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: 'datadoghq.com',
      service: 'falcon-frontend',
      env: config.env,
      version: config.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      defaultPrivacyLevel: 'mask',
      allowedTracingUrls: [(url: string) => new URL(url).pathname.startsWith('/web-api/')],
    });
    rumInitialised = true;
  } catch {
    return false;
  }
  try {
    datadogRum.startSessionReplayRecording();
  } catch {
    // Replay failing must not disable RUM or the browser log correlation.
  }
  try {
    datadogLogs.init({
      clientToken: config.clientToken,
      site: 'datadoghq.com',
      service: 'falcon-frontend',
      env: config.env,
      version: config.version,
    });
  } catch {
    // Log correlation failing must not disable RUM.
  }
  return rumInitialised;
}

/** The signed-in user, as the "Web App Usage" dashboard reads it. */
export interface RumUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyId?: string;
  companyName?: string;
}

/**
 * Attach the signed-in user to every RUM event that follows.
 *
 * The Datadog "Web App Usage" dashboard groups and filters sessions on
 * `@usr.company`, `@usr.name` and `@usr.id` — the attributes Meteor used to
 * send. Without them Falcon sessions drop out of every per-company widget.
 * Only the user's identity goes to Datadog; replay content stays masked.
 *
 * Safe to call when RUM is disabled or not yet initialised (the SDK then
 * ignores it). Never throws.
 */
export function identifyRumUser(user: RumUser): void {
  try {
    datadogRum.setUser({
      id: user.id,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`.trim(),
      ...(user.companyName ? { company: user.companyName } : {}),
      ...(user.companyId ? { company_id: user.companyId } : {}),
    });
  } catch {
    // Telemetry failure must not break the page.
  }
}

/** Forget the user after sign-out so the next session is not attributed to them. */
export function clearRumUser(): void {
  try {
    datadogRum.clearUser();
  } catch {
    // Telemetry failure must not break the page.
  }
}
